import type { ImmutableSourceRevisionRef } from './model';

type RemediationSource = Readonly<{
  documentId: string;
  revisionId: string;
  revisionNumber: number;
  contentHash: string;
  siteId: string;
}>;

export type LegacyRemediationReport = Readonly<{
  source: RemediationSource;
  mimeType: 'application/pdf';
  pageCount: number;
  contentSha256: string;
  pdfBase64: string;
  media: readonly { manifestId: string; sha256: string; phase: 'before' | 'after' }[];
}>;

export type RecordRemediationReport = Readonly<{
  format: 'record-v1';
  schemaVersion: 1;
  source: RemediationSource;
  record: Readonly<{
    findingId: string;
    version: number;
    siteId: string;
    contractorId: string | null;
    contractorName?: string | null;
    workTradeLabel?: string | null;
    title: string | null;
    location: string | null;
    findingDescription: string | null;
    sourceSummary: string | null;
    risk: string | null;
    sourceId: string | null;
    sourceItemId: string | null;
    action: Readonly<{
      submissionId: string | null;
      actionPlan: string;
      description: string;
      recurrencePrevention: string | null;
      submittedAt: string | null;
      submittedByName: string | null;
    }>;
  }>;
  media: readonly Readonly<{
    manifestId: string;
    sha256: string;
    phase: 'before' | 'after';
    siteId: string;
  }>[];
  immutable: true;
}>;

export type RemediationReport = LegacyRemediationReport | RecordRemediationReport;

const object = (value: unknown): Readonly<Record<string, unknown>> | null =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : null;
const requiredText = (value: unknown) => (typeof value === 'string' && value.trim() ? value : null);
const nullableText = (value: unknown) =>
  value == null ? null : typeof value === 'string' && value.trim() ? value : undefined;
const sha256 = (value: unknown) => typeof value === 'string' && /^[a-f0-9]{64}$/u.test(value);

function sourceMatches(
  value: unknown,
  expected: ImmutableSourceRevisionRef,
): value is RemediationSource {
  const source = object(value);
  return Boolean(
    source &&
    expected.sourceDomain === 'inspection' &&
    expected.documentKind === 'inspection-remediation' &&
    expected.scope.kind !== 'tenant' &&
    source.siteId === expected.scope.siteId &&
    source.documentId === expected.documentId &&
    source.revisionId === expected.revisionId &&
    source.revisionNumber === expected.revisionNumber &&
    source.contentHash === expected.contentHash,
  );
}

function parseRecordReport(
  report: Readonly<Record<string, unknown>>,
  expected: ImmutableSourceRevisionRef,
): RecordRemediationReport {
  const record = object(report.record);
  const action = object(record?.action);
  const expectedSiteId = expected.scope.kind === 'tenant' ? null : expected.scope.siteId;
  const media = Array.isArray(report.media) ? report.media.map(object) : [];
  const parsedMedia = media.map((item) => ({
    manifestId: requiredText(item?.manifestId),
    sha256: item?.sha256,
    phase: item?.phase,
    siteId: item?.siteId,
  }));
  if (
    report.format !== 'record-v1' ||
    report.schemaVersion !== 1 ||
    report.immutable !== true ||
    !sourceMatches(report.source, expected) ||
    !record ||
    record.findingId !== expected.documentId ||
    record.version !== expected.revisionNumber ||
    record.siteId !== expectedSiteId ||
    nullableText(record.contractorId) === undefined ||
    nullableText(record.contractorName) === undefined ||
    nullableText(record.workTradeLabel) === undefined ||
    nullableText(record.title) === undefined ||
    nullableText(record.location) === undefined ||
    nullableText(record.findingDescription) === undefined ||
    nullableText(record.sourceSummary) === undefined ||
    nullableText(record.risk) === undefined ||
    nullableText(record.sourceId) === undefined ||
    nullableText(record.sourceItemId) === undefined ||
    !action ||
    nullableText(action.submissionId) === undefined ||
    !requiredText(action.actionPlan) ||
    !requiredText(action.description) ||
    nullableText(action.recurrencePrevention) === undefined ||
    nullableText(action.submittedAt) === undefined ||
    nullableText(action.submittedByName) === undefined ||
    !Array.isArray(report.media) ||
    parsedMedia.some(
      (item) =>
        !item.manifestId ||
        !sha256(item.sha256) ||
        !['before', 'after'].includes(String(item.phase)) ||
        item.siteId !== expectedSiteId,
    )
  )
    throw new Error('조치 기록이 제출된 원본과 일치하지 않습니다.');
  return report as unknown as RecordRemediationReport;
}

export function parseRemediationReport(
  value: unknown,
  expected: ImmutableSourceRevisionRef,
): RemediationReport {
  const report = object(value);
  if (!report) throw new Error('제출된 조치 기록을 확인할 수 없습니다.');
  if (report.format === 'record-v1') return parseRecordReport(report, expected);
  if (
    report.format != null ||
    !sourceMatches(report.source, expected) ||
    report.mimeType !== 'application/pdf' ||
    !Number.isSafeInteger(report.pageCount) ||
    Number(report.pageCount) < 1 ||
    !sha256(report.contentSha256) ||
    typeof report.pdfBase64 !== 'string' ||
    !Array.isArray(report.media)
  )
    throw new Error('조치 보고서가 제출된 기록과 일치하지 않습니다.');
  return report as unknown as LegacyRemediationReport;
}

export async function verifiedApprovalPdf(encoded: string, contentSha256: string): Promise<Blob> {
  const bytes = Uint8Array.from(atob(encoded), (char) => char.charCodeAt(0));
  const digest = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
  if (digest !== contentSha256 || new TextDecoder().decode(bytes.slice(0, 5)) !== '%PDF-')
    throw new Error('출력 문서를 확인하지 못했습니다. 다시 조회해 주세요.');
  return new Blob([bytes], { type: 'application/pdf' });
}
